<template>
  <div
    class="ingroup grid flex-1"
    :class="{
      '!grid-cols-[1fr_40px]': isMobile,
      'ingroup--free': freeQuota,
      'max-w-[975px]': freeQuota,
      'max-w-screen-md': !freeQuota,
    }"
  >
    <TopbarMarketSelector
      v-if="!isMobile"
      v-model="selectedMarketplace"
      :options="marketplaces"
      class="ingroup-action-field"
      @on-marketplace-select="showMarketplaces"
    />
    <TopbarSearchInput
      v-model="keyword"
      class="ingroup-action-field"
      :class="{
        'rounded-[6px]': isMobile,
      }"
      @on-enter="redirectToKeywordTool"
    />
    <TopbarCountrySelector
      v-if="!isMobile"
      v-model="selectedCountry"
      :countries="countries"
      class="ingroup-action-field"
      @on-show-countries="showCountries"
    />
    <TopbarSearchButton
      :disabled="!keyword?.trim()"
      class="ingroup-action-field"
      @on-search="handleOnSearch"
    />
    <div
      v-if="freeQuota"
      class="free-quota-meter-cell"
      :class="{
        'col-span-2 justify-self-end': isMobile,
      }"
    >
      <DailySearchQuotaMeter
        :quota="freeQuota"
        source="Free Keyword Tool Header"
      />
    </div>
    <div
      v-if="quota"
      :class="{
        'hidden flex-col justify-between pl-4 md:flex': !freeQuota,
        'free-quota-legacy-cell': freeQuota,
      }"
    >
      <span class="text-tiny-medium text-[--color-neutral-neutral-light]">Searches:</span>
      <span class="text-small-medium flex items-center gap-1 text-[--color-neutral-neutral-dark]">{{ quota }}<ContextualHelp
        :content="t('layout.tooltip.text')"
        trigger-icon-class="text-[--color-neutral-neutral-light]"
        placement="bottom"
      /></span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useCookies } from '@vueuse/integrations/useCookies';
import { storeToRefs } from 'pinia';
import { useDialog } from 'primevue/usedialog';
import { ref, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import ContextualHelp from '@/common/components/molecules/ContextualHelp';
import DailySearchQuotaMeter from '@/common/components/molecules/DailySearchQuotaMeter';
import { useMobile } from '@/common/composables/application';
import type { Quota } from '@/common/types/shop';
import { useTrackedToast } from '@/composables/useTrackedToast';
import { useSearchParams } from '@/modules/keywords/composables/router';
import TopbarCountrySelector from '@/modules/layout/components/atoms/TopbarCountrySelector';
import TopbarMarketSelector from '@/modules/layout/components/atoms/TopbarMarketSelector';
import TopbarSearchButton from '@/modules/layout/components/atoms/TopbarSearchButton';
import TopbarSearchInput from '@/modules/layout/components/atoms/TopbarSearchInput';
import CountriesSelection from '@/modules/layout/components/organisms/CountriesSelection';
import MarketplacesSelection from '@/modules/layout/components/organisms/MarketplacesSelection';
import { useTopbarStore } from '@/modules/layout/stores/topbar';
import { useLoggerMutation } from '@/queries/logger';

defineProps<{
  freeQuota?: Quota;
  quota?: string;
}>();

const cookies = useCookies(['searchCountry']);

const topbarStore = useTopbarStore();
const { selectedCountry, selectedMarketplace, marketplaces, countries } = storeToRefs(topbarStore);
const dialog = useDialog();
const router = useRouter();
const { searchParams } = useSearchParams();
const toast = useTrackedToast();
const { t } = useI18n();
const { isMobile } = useMobile();

const keyword = ref<string>('');

const { mutateAsync: loggerMutation } = useLoggerMutation();

watchEffect(() => {
  keyword.value = searchParams.value.keyword ?? '';
});

const showMarketplaces = () => {
  dialog.open(MarketplacesSelection, {
    props: {
      modal: true,
      closable: false,
      showHeader: false,
      contentClass: 'p-0 rounded-t-md',
    },
    data: {
      marketplaces: marketplaces.value,
      selectedMarketplace: selectedMarketplace.value,
    },
    onClose: ({ data }) => {
      if (data?.selectedMarketplace) {
        selectedMarketplace.value = data.selectedMarketplace;
      }
    },
  });
};

const showCountries = () => {
  dialog.open(CountriesSelection, {
    props: {
      modal: true,
      closable: false,
      showHeader: false,
      contentClass: 'p-0 rounded-t-md',
    },
    data: {
      countries: countries.value,
      selectedCountry: selectedCountry.value,
    },
    onClose: ({ data }) => {
      if (data?.selectedCountry) {
        selectedCountry.value = data.selectedCountry;
      }
    },
  });
};

async function handleOnSearch() {
  await redirectToKeywordTool();
}

async function redirectToKeywordTool() {
  if (!keyword.value?.trim()) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Please enter a keyword', errorCode: 'KEYWORD_REQUIRED' });
    return;
  }

  cookies.set('searchCountry', selectedCountry.value, { path: '/' });

  await loggerMutation({
    action: '/keyword-tool',
    path: 'keyword-tool-search-topbar',
    value: JSON.stringify({
      keyword: keyword.value.trim(),
      country: selectedCountry.value,
      source: selectedMarketplace.value.code,
    }),
  });

  router.push({
    path: '/keyword-tool',
    query: {
      keyword: keyword.value.trim(),
      country: selectedCountry.value,
      source: selectedMarketplace.value.code,
    },
  });
}
</script>

<style lang="scss" scoped>
.ingroup {
  @apply grid-cols-[140px_400px_118px_40px_70px];

  @container (width <= 920px) {
    @apply grid-cols-[80px_1fr_108px_40px_70px];
  }

  &.ingroup--free {
    @apply grid-cols-[140px_minmax(0,1fr)_118px_40px_257px];

    @container (width <= 920px) {
      @apply grid-cols-[80px_minmax(0,1fr)_108px_40px_257px];
    }

    @container (width <= 725px) {
      @apply grid-cols-[80px_minmax(0,1fr)_108px_40px_70px];

      .free-quota-meter-cell {
        display: none;
      }

      .free-quota-legacy-cell {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        padding-left: 16px;
      }
    }
  }
}

.free-quota-meter-cell {
  padding-left: 16px;
}

.free-quota-legacy-cell {
  display: none;
}

@media (max-width: 768px) {
  .free-quota-meter-cell,
  .free-quota-legacy-cell {
    display: none !important;
  }
}

.ingroup:focus-within .ingroup-action-field {
  outline: 1px solid #336D5E !important;
  outline-offset: 0px !important;
}

.ingroup:hover .ingroup-action-field {
  outline: 1px solid #99B6AF;
  outline-offset: 0px;
}
</style>
